import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackMetaEvent, formatCartItemData } from '../utils/metaPixel';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    // Load cart from localStorage on init
    const savedCart = localStorage.getItem('merchMastersCart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const [isCartOpen, setIsCartOpen] = useState(false);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('merchMastersCart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find(item => item.id === product.id && item.source === product.source);

      if (existingItem) {
        // Increment quantity if item already exists
        return prevCart.map(item =>
          item.id === product.id && item.source === product.source
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // Add new item with quantity 1
        return [...prevCart, { ...product, quantity: 1 }];
      }
    });

    // Track AddToCart event
    trackMetaEvent('AddToCart', formatCartItemData(product));

    setIsCartOpen(true); // Open cart drawer when item added
  };

  const removeFromCart = (productId, source) => {
    setCart((prevCart) => prevCart.filter(item => !(item.id === productId && item.source === source)));
  };

  const updateQuantity = (productId, source, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId, source);
      return;
    }

    setCart((prevCart) =>
      prevCart.map(item =>
        item.id === productId && item.source === source
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const DELIVERY_FEE = 400;

  const getOrderTotal = () => {
    return getCartTotal() + DELIVERY_FEE;
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    getOrderTotal,
    DELIVERY_FEE,
    isCartOpen,
    setIsCartOpen,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
